import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../supabaseClient';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    loanType: '',
    loanAmount: '',
    loanPurpose: '',
    message: '',
    agreeToSms: false,
    agreeToTcpa: false
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreeToTcpa) {
      setStatus({
        type: 'error',
        message: 'Please agree to the TCPA consent to continue.'
      });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const { error } = await supabase
        .from('ursus_contacts')
        .insert([{
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          loan_type: formData.loanType,
          loan_amount: formData.loanAmount,
          loan_purpose: formData.loanPurpose,
          message: formData.message,
          agree_to_sms: formData.agreeToSms,
          agree_to_tcpa: formData.agreeToTcpa,
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;

      setStatus({
        type: 'success',
        message: 'Thank you! Your request has been received. We will contact you within 24 hours to discuss your loan application support needs.'
      });

      setFormData({
        fullName: '',
        email: '',
        phone: '',
        loanType: '',
        loanAmount: '',
        loanPurpose: '',
        message: '',
        agreeToSms: false,
        agreeToTcpa: false
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus({
        type: 'error',
        message: 'There was an error submitting your request. Please try again or email us directly at support@ursusconnects.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - Ursus Victor LLC | Loan Application Support</title>
        <meta name="description" content="Contact Ursus Victor LLC for professional loan application support services. Get help with your loan documentation today." />
      </Helmet>

      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
        color: 'white',
        padding: '4rem 2rem',
        textAlign: 'center'
      }}>
        <div style={{maxWidth: '800px', margin: '0 auto'}}>
          <h1 style={{fontSize: '3rem', fontWeight: '900', marginBottom: '1rem'}}>
            Get Application Support
          </h1>
          <p style={{fontSize: '1.3rem', opacity: 0.95, lineHeight: '1.6'}}>
            Let us help you prepare your loan application and documentation
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section style={{padding: '5rem 2rem', background: '#f8fafc'}}>
        <div style={{maxWidth: '900px', margin: '0 auto'}}>
          {/* Contact Info */}
          <div style={{
            background: 'white',
            padding: '2.5rem',
            borderRadius: '16px',
            marginBottom: '3rem',
            border: '2px solid #e2e8f0',
            textAlign: 'center'
          }}>
            <h2 style={{fontSize: '2rem', fontWeight: '800', color: '#1e40af', marginBottom: '2rem'}}>
              Contact Information
            </h2>
            <div style={{display: 'grid', gap: '1.5rem', fontSize: '1.1rem', color: '#475569'}}>
              <div>
                <strong>📧 Email:</strong> <a href="mailto:support@ursusconnects.com" style={{color: '#3b82f6', fontWeight: '600'}}>support@ursusconnects.com</a>
              </div>
              <div>
                <strong>📧 General Inquiries:</strong> <a href="mailto:info@ursusconnects.com" style={{color: '#3b82f6', fontWeight: '600'}}>info@ursusconnects.com</a>
              </div>
              <div>
                <strong>⏰ Response Time:</strong> Within 24 hours
              </div>
              <div>
                <strong>🕐 Business Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM EST
              </div>
            </div>
          </div>

          {/* Form */}
          <div style={{
            background: 'white',
            padding: '3rem',
            borderRadius: '16px',
            border: '2px solid #e2e8f0',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
          }}>
            <h2 style={{fontSize: '2.2rem', fontWeight: '800', color: '#1e40af', marginBottom: '1.5rem', textAlign: 'center'}}>
              Request Application Support
            </h2>

            {status.message && (
              <div style={{
                padding: '1.25rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                background: status.type === 'success' ? '#dbeafe' : '#fee2e2',
                color: status.type === 'success' ? '#1e40af' : '#991b1b',
                border: `2px solid ${status.type === 'success' ? '#3b82f6' : '#ef4444'}`,
                fontWeight: '600'
              }}>
                {status.message}
              </div>
            )}

            <form onSubmit={handleSubmit} style={{display: 'grid', gap: '1.75rem'}}>
              <div>
                <label style={{display: 'block', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem', fontSize: '1.05rem'}}>
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '1rem',
                    border: '2px solid #cbd5e1',
                    borderRadius: '10px',
                    fontSize: '1.05rem'
                  }}
                />
              </div>

              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem'}}>
                <div>
                  <label style={{display: 'block', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem', fontSize: '1.05rem'}}>
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '1rem',
                      border: '2px solid #cbd5e1',
                      borderRadius: '10px',
                      fontSize: '1.05rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{display: 'block', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem', fontSize: '1.05rem'}}>
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '1rem',
                      border: '2px solid #cbd5e1',
                      borderRadius: '10px',
                      fontSize: '1.05rem'
                    }}
                  />
                </div>
              </div>

              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem'}}>
                <div>
                  <label style={{display: 'block', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem', fontSize: '1.05rem'}}>
                    Loan Type *
                  </label>
                  <select
                    name="loanType"
                    value={formData.loanType}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '1rem',
                      border: '2px solid #cbd5e1',
                      borderRadius: '10px',
                      fontSize: '1.05rem'
                    }}
                  >
                    <option value="">Select Type</option>
                    <option value="personal-loan">Personal Loan</option>
                    <option value="business-loan">Business Loan</option>
                    <option value="mortgage">Mortgage</option>
                    <option value="auto-loan">Auto Loan</option>
                    <option value="student-loan">Student Loan</option>
                    <option value="home-equity">Home Equity</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label style={{display: 'block', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem', fontSize: '1.05rem'}}>
                    Loan Amount *
                  </label>
                  <select
                    name="loanAmount"
                    value={formData.loanAmount}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '1rem',
                      border: '2px solid #cbd5e1',
                      borderRadius: '10px',
                      fontSize: '1.05rem'
                    }}
                  >
                    <option value="">Select Range</option>
                    <option value="under-10k">Under $10,000</option>
                    <option value="10k-25k">$10,000 - $25,000</option>
                    <option value="25k-50k">$25,000 - $50,000</option>
                    <option value="50k-100k">$50,000 - $100,000</option>
                    <option value="100k-250k">$100,000 - $250,000</option>
                    <option value="250k-500k">$250,000 - $500,000</option>
                    <option value="over-500k">Over $500,000</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{display: 'block', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem', fontSize: '1.05rem'}}>
                  Loan Purpose
                </label>
                <input
                  type="text"
                  name="loanPurpose"
                  value={formData.loanPurpose}
                  onChange={handleChange}
                  placeholder="e.g., Home purchase, Business expansion, Debt consolidation"
                  style={{
                    width: '100%',
                    padding: '1rem',
                    border: '2px solid #cbd5e1',
                    borderRadius: '10px',
                    fontSize: '1.05rem'
                  }}
                />
              </div>

              <div>
                <label style={{display: 'block', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem', fontSize: '1.05rem'}}>
                  Additional Information
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  style={{
                    width: '100%',
                    padding: '1rem',
                    border: '2px solid #cbd5e1',
                    borderRadius: '10px',
                    fontSize: '1.05rem',
                    fontFamily: 'inherit'
                  }}
                  placeholder="Tell us about your loan application needs and any specific documentation support you require..."
                />
              </div>

              {/* TCPA Consent */}
              <div style={{
                background: '#fef3c7',
                padding: '1.75rem',
                borderRadius: '12px',
                border: '3px solid #f59e0b'
              }}>
                <label style={{display: 'flex', gap: '1rem', alignItems: 'flex-start', cursor: 'pointer'}}>
                  <input
                    type="checkbox"
                    name="agreeToTcpa"
                    checked={formData.agreeToTcpa}
                    onChange={handleChange}
                    required
                    style={{marginTop: '0.25rem', width: '20px', height: '20px'}}
                  />
                  <span style={{fontSize: '1rem', color: '#78350f', lineHeight: '1.7', fontWeight: '600'}}>
                    <strong>Required:</strong> By submitting this form, I agree to receive SMS updates from Ursus Victor LLC 
                    regarding my loan application support request, document requirements, and application status updates. 
                    I understand that consent is not required to use our services and I may opt out at any time by replying STOP. 
                    Message and data rates may apply. View our <a href="/sms-policy" style={{color: '#1e40af', textDecoration: 'underline'}}>SMS Policy</a> and{' '}
                    <a href="/privacy" style={{color: '#1e40af', textDecoration: 'underline'}}>Privacy Policy</a>.
                  </span>
                </label>
              </div>

              {/* Optional SMS */}
              <div style={{
                background: '#dbeafe',
                padding: '1.75rem',
                borderRadius: '12px',
                border: '2px solid #3b82f6'
              }}>
                <label style={{display: 'flex', gap: '1rem', alignItems: 'flex-start', cursor: 'pointer'}}>
                  <input
                    type="checkbox"
                    name="agreeToSms"
                    checked={formData.agreeToSms}
                    onChange={handleChange}
                    style={{marginTop: '0.25rem', width: '20px', height: '20px'}}
                  />
                  <span style={{fontSize: '1rem', color: '#1e40af', lineHeight: '1.7', fontWeight: '600'}}>
                    <strong>Optional:</strong> I would like to receive additional SMS messages from Ursus Victor LLC including 
                    financial tips, loan preparation advice, partner offers from financial institutions, and promotional information. 
                    I understand this is optional and I can opt out anytime by replying STOP. Msg & data rates may apply. Message frequency varies.
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  background: isSubmitting ? '#94a3b8' : 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                  color: 'white',
                  padding: '1.25rem 2rem',
                  borderRadius: '12px',
                  fontSize: '1.2rem',
                  fontWeight: '800',
                  border: 'none',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: '0 4px 12px rgba(30,64,175,0.3)'
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Request Application Support'}
              </button>

              <p style={{fontSize: '0.95rem', color: '#64748b', textAlign: 'center'}}>
                * Required fields
              </p>
            </form>
          </div>

          {/* Disclaimer */}
          <div style={{
            marginTop: '3rem',
            padding: '2.5rem',
            background: '#fef3c7',
            borderRadius: '16px',
            border: '3px solid #f59e0b'
          }}>
            <h3 style={{fontSize: '1.3rem', fontWeight: '800', color: '#92400e', marginBottom: '1rem'}}>
              Important Disclosure
            </h3>
            <p style={{fontSize: '1rem', color: '#78350f', lineHeight: '1.8', fontWeight: '600'}}>
              Ursus Victor LLC provides loan application support and documentation services only. We are NOT a lender, 
              do NOT make loans, and do NOT make lending decisions. We do NOT guarantee loan approval. Submission of 
              this form is a request for application support services only. Your information will be kept confidential 
              and used solely for providing application assistance.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
